import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CalendarDays, Mail, Clock, Languages, Sun, Reply, Trash2, LogOut, Sparkles } from "lucide-react";
import { api, clearToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Agenda() {
  const navigate = useNavigate();
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/appointments")
      .then(({ data }) => setAppts(data))
      .catch((e) => {
        if (e?.response?.status === 401) navigate("/login");
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const logout = () => {
    clearToken();
    navigate("/login");
  };

  const grouped = appts.reduce((acc, a) => {
    (acc[a.date] = acc[a.date] || []).push(a);
    return acc;
  }, {});

  const todayStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  const todayCount = appts.filter((a) => a.date === todayStr).length;

  const replyTemplates = {
    fr: { s: "Votre rendez-vous — Solaria (Euroconsult)", b: (n, d, t) => `Bonjour ${n},\n\nMerci pour votre demande de rendez-vous du ${d} à ${t}. Je reviens vers vous pour confirmer et préparer votre étude gratuite.\n\nBien à vous,\nSolaria — Euroconsult\nGSM : 0472 19 52 45` },
    en: { s: "Your appointment — Solaria (Euroconsult)", b: (n, d, t) => `Hello ${n},\n\nThank you for your appointment request on ${d} at ${t}. I'm getting back to you to confirm and prepare your free assessment.\n\nKind regards,\nSolaria — Euroconsult\nPhone: +32 472 19 52 45` },
    nl: { s: "Uw afspraak — Solaria (Euroconsult)", b: (n, d, t) => `Beste ${n},\n\nBedankt voor uw afspraakaanvraag op ${d} om ${t}. Ik neem contact met u op om te bevestigen en uw gratis studie voor te bereiden.\n\nMet vriendelijke groeten,\nSolaria — Euroconsult\nGSM: 0472 19 52 45` },
    es: { s: "Su cita — Solaria (Euroconsult)", b: (n, d, t) => `Hola ${n},\n\nGracias por su solicitud de cita el ${d} a las ${t}. Me pondré en contacto para confirmar y preparar su estudio gratuito.\n\nUn saludo,\nSolaria — Euroconsult\nTel.: +32 472 19 52 45` },
    de: { s: "Ihr Termin — Solaria (Euroconsult)", b: (n, d, t) => `Hallo ${n},\n\nvielen Dank für Ihre Terminanfrage am ${d} um ${t}. Ich melde mich zur Bestätigung und zur Vorbereitung Ihrer kostenlosen Analyse.\n\nMit freundlichen Grüßen,\nSolaria — Euroconsult\nTel.: +32 472 19 52 45` },
  };

  const replyLink = (a) => {
    const tpl = replyTemplates[(a.language || "fr").toLowerCase()] || replyTemplates.fr;
    const dateFr = new Date(a.date + "T00:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
    return `mailto:${a.email}?subject=${encodeURIComponent(tpl.s)}&body=${encodeURIComponent(tpl.b(a.name, dateFr, a.time))}`;
  };

  const deleteAppointment = async (id) => {
    if (!window.confirm("Supprimer définitivement ce rendez-vous ?")) return;
    try {
      await api.delete(`/appointments/${id}`);
      setAppts((prev) => prev.filter((a) => a.id !== id));
      toast.success("Rendez-vous supprimé.");
    } catch (e) {
      toast.error("Suppression impossible.");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-primary px-6 py-6 text-primary-foreground">
        <div className="mx-auto flex max-w-4xl items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent">
              <Sun className="h-5 w-5" />
            </span>
            <div>
              <h1 className="font-display text-xl font-bold">Mon agenda</h1>
              <p className="text-sm text-primary-foreground/70">Rendez-vous pris par l'assistante IA</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild variant="secondary" size="sm" className="rounded-full">
              <Link to="/studio" data-testid="agenda-studio-link">
                <Sparkles className="mr-1.5 h-4 w-4" /> Studio Marketing
              </Link>
            </Button>
            <Button onClick={logout} variant="secondary" size="sm" className="rounded-full" data-testid="logout-button">
              <LogOut className="mr-1.5 h-4 w-4" /> Déconnexion
            </Button>
            <Button asChild variant="secondary" size="sm" className="rounded-full">
              <Link to="/" data-testid="back-home-link">
                <ArrowLeft className="mr-1.5 h-4 w-4" /> Accueil
              </Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-12">
        <div className="mb-8 grid gap-4 sm:grid-cols-2">
          <div className="flex items-center gap-3 rounded-2xl border border-border bg-accent p-5 text-accent-foreground" data-testid="today-count-card">
            <Clock className="h-6 w-6" />
            <div>
              <p className="font-display text-2xl font-bold" data-testid="today-appointments-count">{todayCount}</p>
              <p className="text-sm opacity-90">rendez-vous aujourd'hui</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-2xl border border-border bg-card p-5">
            <CalendarDays className="h-6 w-6 text-accent" />
            <div>
              <p className="font-display text-2xl font-bold" data-testid="appointments-count">{appts.length}</p>
              <p className="text-sm text-muted-foreground">rendez-vous programmés</p>
            </div>
          </div>
        </div>

        {loading ? (
          <p className="text-muted-foreground">Chargement...</p>
        ) : appts.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-border bg-card p-16 text-center">
            <CalendarDays className="mx-auto h-12 w-12 text-muted-foreground/40" />
            <p className="mt-4 font-display text-lg font-semibold">Aucun rendez-vous pour l'instant</p>
            <p className="mt-1 text-muted-foreground">Les rendez-vous réservés sur le site apparaîtront ici.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(grouped).map(([date, list]) => (
              <motion.div key={date} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
                <h2 className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  {new Date(date + "T00:00:00").toLocaleDateString("fr-FR", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </h2>
                <div className="space-y-3">
                  {list.map((a) => (
                    <div
                      key={a.id}
                      data-testid="appointment-card"
                      className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-5 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-xl bg-secondary text-secondary-foreground">
                          <Clock className="h-4 w-4" />
                          <span className="mt-0.5 text-sm font-bold">{a.time}</span>
                        </div>
                        <div>
                          <p className="font-display text-lg font-bold">{a.name}</p>
                          <a
                            href={`mailto:${a.email}`}
                            data-testid="appointment-email"
                            className="flex items-center gap-1.5 text-sm text-muted-foreground underline-offset-2 transition-colors hover:text-accent hover:underline"
                          >
                            <Mail className="h-3.5 w-3.5" /> {a.email}
                          </a>
                          {a.note && <p className="mt-1 text-sm text-muted-foreground">"{a.note}"</p>}
                        </div>
                      </div>
                      <div className="flex flex-col items-start gap-2 sm:items-end">
                        <span className="flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-xs font-medium uppercase">
                          <Languages className="h-3 w-3" /> {a.language}
                        </span>
                        <Button asChild size="sm" className="rounded-full bg-accent hover:bg-accent/90">
                          <a href={replyLink(a)} data-testid="reply-client-button">
                            <Reply className="mr-1.5 h-4 w-4" /> Répondre au client
                          </a>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteAppointment(a.id)}
                          data-testid="delete-appointment-button"
                          className="rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        >
                          <Trash2 className="mr-1.5 h-4 w-4" /> Supprimer
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
